import { ApplicationProductTypeEnum } from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import SectionedContentScreen, {
  SectionedContentScreenModel,
} from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import useAgreements from 'shared/hooks/useAgreements/useAgreements';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import {
  showZendeskSection,
  ZendeskSections,
} from 'shared/utils/zendesk/zendeskUtils';

const ManageAdvanceAccountScreen = () => {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'manageAdvanceAccount',
  });

  useAgreements([ApplicationProductTypeEnum.Advance]);

  const handleAdvanceHistoryAction = () => {
    router.push({
      pathname: `/accounts/advance-details/history`,
      params: { id },
    });
  };

  const handleAdvanceStatementAction = () => {
    router.push({
      pathname: `/accounts/advance-details/statements`,
      params: {
        id: id,
      },
    });
  };

  const handleWhatIsVaroAdvanceAction = () => {
    router.push({
      pathname: '/accounts/advance-details/what-is-va',
    });
  };

  const handleVaroAdvanceFAQAction = () => {
    showZendeskSection(ZendeskSections.VARO_ADVANCE_FAQ);
  };

  const handleTermsAndConditionAction = () => {
    router.push({
      pathname: '/terms-and-conditions',
      params: {
        agreementType: ApplicationProductTypeEnum.Advance,
      },
    });
  };

  const sectionsList: SectionedContentScreenModel = {
    sections: [
      {
        sectionHeader: {
          id: 'ManageAccount',
          name: VXLComponentId.SECTION_DIVIDER,
          props: {
            labelText: 'Manage account',
          },
        },

        data: [
          {
            id: 'ADVANCE_HISTORY',
            name: VXLComponentId.LIST_ITEM,
            props: {
              primaryCopy: translatedText(
                'listItemScreenOptions.advanceHistory',
              ),
              onPress: handleAdvanceHistoryAction,
              showsChevron: true,
              analyticsId: 'advanceHistoryClicked',
              customAttributes: {
                account_type: 'advance',
              },
            },
          },
          {
            id: 'MONTHLY_STATEMENT',
            name: VXLComponentId.LIST_ITEM,
            props: {
              primaryCopy: translatedText(
                'listItemScreenOptions.monthlyStatements',
              ),
              showsChevron: true,
              onPress: handleAdvanceStatementAction,
              analyticsId: 'statementsAndDocumentsClicked',
            },
          },
        ],
      },
      {
        sectionHeader: {
          id: 'help',
          name: VXLComponentId.SECTION_DIVIDER,
          props: {
            labelText: 'Help',
          },
        },
        data: [
          {
            id: 'WHAT_IS_VARO_ADVANCE',
            name: VXLComponentId.LIST_ITEM,
            props: {
              primaryCopy: translatedText(
                'listItemScreenOptions.whatIsVaroAdvance',
              ),
              showsChevron: true,
              onPress: handleWhatIsVaroAdvanceAction,
              analyticsId: 'what-is-va',
              customAttributes: {
                faq_name: 'va_intro',
              },
            },
          },
          {
            id: 'VARO_ADVANCE_FAQ',
            name: VXLComponentId.LIST_ITEM,
            props: {
              primaryCopy: translatedText(
                'listItemScreenOptions.varoAdvanceFAQs',
              ),
              showsChevron: true,
              onPress: handleVaroAdvanceFAQAction,
              analyticsId: 'va-faq',
              customAttributes: {
                faq_name: 'va_faqs',
              },
            },
          },
          {
            id: 'TERMS_AND_CONDITIONS',
            name: VXLComponentId.LIST_ITEM,
            props: {
              primaryCopy: translatedText(
                'listItemScreenOptions.termsAndConditions',
              ),
              showsChevron: true,
              onPress: handleTermsAndConditionAction,
              analyticsId: 'termsAndConditions',
              customAttributes: {
                faq_name: 'terms_and_conditions',
              },
            },
          },
        ],
      },
    ],
  };

  return (
    <View style={styles.screen}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: translatedText('title'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />

      <SectionedContentScreen model={sectionsList} />
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    paddingVertical: theme.spacing.m,
  },
});

export default ManageAdvanceAccountScreen;
